// ─────────────────────────────────────────────────────────────
// TradeChecklist
// Extracted from App.js.
//
// Enhancement: after "Get My Answer", the result screen shows
// a "Your Patterns to Watch" block pulled from detector_state
// + detector_snapshots. These are the user's top active
// behavioral patterns — surfaced at the exact moment they've
// confirmed they're mentally ready to trade.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo } from "react";
import { useDetectors } from "../../hooks/useDetectors";
import { useUserRules } from "../../hooks/useUserRules";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { useCalendar } from "../../hooks/useCalendar";

const CATEGORY_ICONS = {
  Physical:      "💪",
  Environmental: "🏠",
  Mental:        "🧠",
  Emotional:     "❤️",
  Discipline:    "🛡️",
};

const CATEGORY_ORDER = ["Physical", "Environmental", "Mental", "Emotional", "Discipline"];

// ─── Behavioral patterns hook ─────────────────────────────────
// Queries detector_state (active/working_on_it) joined with the
// most recent detector_snapshots metric_value for each detector.
// Returns the top 3 patterns sorted by severity (metric_value desc).

function useActivePatterns(userId, accountId = null) {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        // 1. Fetch active detector states (not dismissed)
        const acctFilter = accountId
          ? `account_id=eq.${accountId}`
          : `account_id=is.null`;

        const stateRes = await authFetch(
          `${SUPABASE_URL}/rest/v1/detector_state` +
          `?user_id=eq.${userId}` +
          `&${acctFilter}` +
          `&status=in.(active,working_on_it)` +
          `&select=detector_id,status,notes`
        );
        if (!stateRes.ok) return;
        const states = await stateRes.json();
        if (!states.length) { if (!cancelled) { setPatterns([]); setLoading(false); } return; }

        // 2. Fetch recent snapshots for each active detector
        const detectorIds = states.map(s => `"${s.detector_id}"`).join(",");
        const since = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

        const snapRes = await authFetch(
          `${SUPABASE_URL}/rest/v1/detector_snapshots` +
          `?user_id=eq.${userId}` +
          `&${acctFilter}` +
          `&detector_id=in.(${detectorIds})` +
          `&snapshot_date=gte.${since}` +
          `&select=detector_id,metric_name,metric_value,snapshot_date,confidence,n` +
          `&order=snapshot_date.desc` +
          `&limit=500`
        );
        const snaps = snapRes.ok ? await snapRes.json() : [];

        // Latest snapshot per detector (already sorted desc)
        const latestByDetector = {};
        for (const s of snaps) {
          if (!latestByDetector[s.detector_id]) latestByDetector[s.detector_id] = s;
        }

        // Merge state + snapshot, sort worst first, cap at 3
        const merged = states
          .map(st => ({
            detectorId:  st.detector_id,
            status:      st.status,
            notes:       st.notes,
            metricValue: latestByDetector[st.detector_id]?.metric_value ?? null,
            metricName:  latestByDetector[st.detector_id]?.metric_name  ?? null,
            confidence:  latestByDetector[st.detector_id]?.confidence   ?? null,
            n:           latestByDetector[st.detector_id]?.n             ?? null,
          }))
          .sort((a, b) => (b.metricValue ?? 0) - (a.metricValue ?? 0))
          .slice(0, 3);

        if (!cancelled) setPatterns(merged);
      } catch (e) {
        console.warn("[TradeChecklist] pattern load failed:", e.message);
        if (!cancelled) setPatterns([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [userId, accountId]);

  return { patterns, loading };
}

// ─── Detector label map ───────────────────────────────────────
const DETECTOR_LABELS = {
  revenge_trading:     "Revenge Trading",
  overtrading:         "Overtrading",
  cut_winners_early:   "Cutting Winners Early",
  letting_losers_run:  "Letting Losers Run",
  stop_loss_moving:    "Moving Stop Losses",
  position_size_creep: "Position Size Creep",
  tilt_trading:        "Tilt Trading",
  news_trading:        "Trading Around News",
  discipline_decline:  "Discipline Declining",
  fomo_entries:        "FOMO Entries",
  emotional_trading:   "Emotional Trading",
  early_exits:         "Exiting Too Early",
  overconfidence:      "Overconfidence",
  loss_aversion:       "Loss Aversion",
};

function detectorLabel(id) {
  return DETECTOR_LABELS[id] || id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function patternWarningText(p) {
  const label = detectorLabel(p.detectorId);
  const pct   = p.metricValue !== null
    ? `${Math.round(p.metricValue * (p.metricValue <= 1 ? 100 : 1))}%`
    : null;
  const n    = p.n ? ` across ${p.n} trades` : "";
  const conf = p.confidence === "high" ? " — high confidence" : "";
  if (pct) return `${label} detected in ${pct} of recent sessions${n}${conf}.`;
  return `${label} pattern is active in your recent history.`;
}

// ─── Patterns block ───────────────────────────────────────────
// ─── Cost lookup helper ──────────────────────────────────────────────────────
// Finds the estimated monthly cost for a detector_id in allDetectorResults.
// Returns null if no cost exists so callers can conditionally render.
function getDetectorCost(allDetectorResults, detectorId) {
  if (!allDetectorResults) return null;
  for (const suiteKey of ["behavior", "risk", "psychology", "context"]) {
    const suite = allDetectorResults[suiteKey];
    if (!suite?.detectors) continue;
    for (const det of suite.detectors) {
      if (det.id === detectorId || det.detector_id === detectorId) {
        const cost = det.estimated_cost ?? null;
        return (cost && cost > 0) ? cost : null;
      }
    }
  }
  return null;
}

function ActivePatternsBlock({ allDetectorResults, detectorsLoading }) {
  // Derive top 3 costliest patterns directly from allDetectorResults.
  // This ensures pre-flight reflects the same data as the Analytics page —
  // not detector_state which is empty until the user manually interacts
  // with the detector actions menu.
  const topPatterns = useMemo(() => {
    if (!allDetectorResults) return null; // still loading
    const CONFIDENT = new Set(["moderate", "strong"]);
    const found = [];
    for (const suiteKey of ["behavior", "risk", "psychology", "context"]) {
      const suite = allDetectorResults[suiteKey];
      if (!suite?.detectors) continue;
      for (const det of suite.detectors) {
        const cost = det.estimated_cost ?? 0;
        // Only include patterns with meaningful cost AND sufficient confidence.
        // Filters out Psychology/Context detectors that fire on thin data.
        if (cost > 0 && CONFIDENT.has(det.confidence)) {
          found.push({
            id:    det.id || det.detector_id,
            label: det.label || detectorLabel(det.id || det.detector_id),
            cost,
            confidence: det.confidence,
          });
        }
      }
    }
    return found
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 3);
  }, [allDetectorResults]);

  // Still loading — edge function call in flight
  if (detectorsLoading || topPatterns === null) return (
    <div style={{ padding: "12px 0", textAlign: "center", fontSize: 12, color: C.muted }}>
      Checking your patterns...
    </div>
  );

  // No costly patterns detected
  if (!topPatterns.length) return (
    <div style={{
      padding: "12px 16px",
      background: C.green + "10",
      border: `1px solid ${C.green}25`,
      borderRadius: 10,
      textAlign: "center",
      fontSize: 13,
      color: C.green,
    }}>
      🎯 No active behavioral patterns detected. Clean slate.
    </div>
  );

  const fmtCost = (cost) => cost >= 1000
    ? `$${(cost / 1000).toFixed(1)}k`
    : `$${Math.round(cost)}`;

  return (
    <div>
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 12, paddingBottom: 10,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{ fontSize: 16 }}>🔍</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Your Patterns to Watch</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
            Top 3 by estimated cost (moderate or strong confidence) — from your Analytics.
          </div>
        </div>
      </div>

      {/* Pattern rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {topPatterns.map(p => (
          <div key={p.id} style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            padding: "10px 14px",
            background: C.red + "08",
            border: `1px solid ${C.red}20`,
            borderLeft: `3px solid ${C.red}`,
            borderRadius: 8,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {p.label}
                </span>
                <span style={{ fontSize: 12, color: C.red, fontFamily: "monospace", fontWeight: 700, flexShrink: 0 }}>
                  Est. cost: −{fmtCost(p.cost)}/mo
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                Stay aware of this pattern today. Check Analytics for the full breakdown.
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginTop: 10, textAlign: "center", lineHeight: 1.6 }}>
        Showing top 3 by estimated cost (moderate or strong confidence only). Thin/insufficient patterns excluded. See Analytics for the full picture.
        Estimated costs reflect patterns in your recent trade history.
      </div>
    </div>
  );
}

// ─── Strengths block ───────────────────────────────────────────────────────
// Mirrors ActivePatternsBlock, but surfaces the trader's confirmed strengths
// instead of costly patterns.
//
// IMPORTANT — this is NOT a true dollar-for-dollar mirror of "Patterns to
// Watch": detectors only carry `estimated_cost` (a drain figure). There is
// no `estimated_gain`/edge-value field in the detector payload, so there's
// nothing to rank strengths by in dollars. Instead we surface detectors that
// fired with real signal (moderate/strong confidence, enough trades to
// trust) but measured ZERO cost — i.e. confirmed clean, not "estimated to
// be earning you $X". Ranked by confidence tier then sample size, since
// that's the only ordering the data actually supports. Framing this as a
// dollar figure would overclaim what the detector suite measures.
function StrengthsBlock({ allDetectorResults, detectorsLoading }) {
  const topStrengths = useMemo(() => {
    if (!allDetectorResults) return null; // still loading
    const CONFIDENT = new Set(["moderate", "strong"]);
    const found = [];
    for (const suiteKey of ["behavior", "risk", "psychology", "context"]) {
      const suite = allDetectorResults[suiteKey];
      if (!suite?.detectors) continue;
      for (const det of suite.detectors) {
        const cost = det.estimated_cost ?? 0;
        // Zero cost + real confidence + NOT flagged = a pattern that fired
        // but isn't draining you AND isn't a live behavioral concern. The
        // `flagged` check matters: some detectors (time_in_trade,
        // r_multiples, size_tilt, etc.) can report zero estimated_cost
        // while still describing a real problem in their summary text —
        // without this check those would show up here as false strengths.
        if (cost === 0 && !det.flagged && CONFIDENT.has(det.confidence)) {
          found.push({
            id:         det.id || det.detector_id,
            title:      det.title || detectorLabel(det.id || det.detector_id),
            summary:    det.summary || null,
            confidence: det.confidence,
            n:          det.n || 0,
          });
        }
      }
    }
    const tierRank = { strong: 0, moderate: 1 };
    return found
      .sort((a, b) => (tierRank[a.confidence] - tierRank[b.confidence]) || (b.n - a.n))
      .slice(0, 3);
  }, [allDetectorResults]);

  if (detectorsLoading || topStrengths === null) return (
    <div style={{ padding: "12px 0", textAlign: "center", fontSize: 12, color: C.muted }}>
      Checking your strengths...
    </div>
  );

  if (!topStrengths.length) return (
    <div style={{
      padding: "12px 16px",
      background: C.blue + "0a",
      border: `1px solid ${C.blue}25`,
      borderRadius: 10,
      textAlign: "center",
      fontSize: 13,
      color: C.muted,
    }}>
      No confirmed strengths yet — either you need more logged trades, or every
      pattern with enough confidence is still flagged as a concern. Check Analytics.
    </div>
  );

  return (
    <div>
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 12, paddingBottom: 10,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{ fontSize: 16 }}>✨</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Your Strengths</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
            Top 3 moderate-or-strong patterns with zero measured cost — from your Analytics.
          </div>
        </div>
      </div>

      {/* Strength rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {topStrengths.map(s => (
          <div key={s.id} style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            padding: "10px 14px",
            background: C.green + "08",
            border: `1px solid ${C.green}20`,
            borderLeft: `3px solid ${C.green}`,
            borderRadius: 8,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>✓</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {s.title}
                </span>
                <span style={{ fontSize: 12, color: C.green, fontFamily: "monospace", fontWeight: 700, flexShrink: 0 }}>
                  No measured drain
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                {s.summary || "Confirmed clean in your recent trades. Keep doing what you're doing here."}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginTop: 10, textAlign: "center", lineHeight: 1.6 }}>
        Showing top 3 by confidence + sample size among patterns with zero measured cost. See Analytics for the full picture.
      </div>
    </div>
  );
}

// ─── Today's Trading Window ───────────────────────────────────
// Appended to the result screen. Shows calendar-aware time blocks
// for today, color-coded clean/caution/avoid based on high-impact
// event proximity (±30 min buffer).

const TRADING_BLOCKS = [
  { id: "pre_open",   label: "Pre-Open",     startH: 8,  startM: 0,  endH: 9,  endM: 30  },
  { id: "open",       label: "Market Open",  startH: 9,  startM: 30, endH: 10, endM: 30  },
  { id: "mid_morn",   label: "Mid-Morning",  startH: 10, startM: 30, endH: 12, endM: 0   },
  { id: "lunch",      label: "Lunch",        startH: 12, startM: 0,  endH: 13, endM: 30  },
  { id: "afternoon",  label: "Afternoon",    startH: 13, startM: 30, endH: 16, endM: 0   },
  { id: "late",       label: "Late Session", startH: 16, startM: 0,  endH: 17, endM: 0   },
];

const BUFFER_MS = 30 * 60 * 1000; // 30 min buffer around events

function blockToMinutes(h, m) { return h * 60 + m; }

function getBlockStatus(block, highImpactEvents) {
  // Convert ET wall-clock block times to UTC using the simplest reliable method:
  // format a known UTC moment in ET to discover the current ET offset,
  // then apply that offset to the block times.
  const now    = new Date();
  const etOpts = { timeZone: "America/New_York" };
  const todayET = now.toLocaleDateString("en-CA", etOpts);

  // Ask America/New_York directly what its current UTC offset is.
  // Returns "-04:00" (EDT, summer) or "-05:00" (EST, winter).
  // DST-safe, server-timezone-safe, works anywhere in the world.
  const getETOffset = () => {
    const etStr = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      timeZoneName: "shortOffset",
    }).format(new Date());
    return etStr.includes("GMT-4") ? "-04:00" : "-05:00";
  };

  const etOffset = getETOffset();

  // Build block UTC times by appending the ET offset directly to the ISO string.
  // JavaScript parses "2026-07-07T09:30:00-04:00" correctly to UTC.
  const toUTC = (h, m) =>
    new Date(`${todayET}T${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:00${etOffset}`);

  const bsUTC = toUTC(block.startH, block.startM);
  const beUTC = toUTC(block.endH, block.endM);

  let triggerEvent = null;
  let status = "clean";

  for (const ev of highImpactEvents) {
    const evMs = ev.dt.getTime();
    // Avoid zone: event falls within block ± buffer
    if (evMs >= bsUTC.getTime() - BUFFER_MS && evMs <= beUTC.getTime() + BUFFER_MS) {
      // Danger if event is actually inside the block or within 15 min
      const innerBuffer = 15 * 60 * 1000;
      if (evMs >= bsUTC.getTime() - innerBuffer && evMs <= beUTC.getTime() + innerBuffer) {
        status = "avoid";
      } else if (status !== "avoid") {
        status = "caution";
      }
      if (!triggerEvent) triggerEvent = ev;
    }
  }

  return { status, triggerEvent, bsUTC, beUTC };
}

function TodaysTradingWindow() {
  const { todayEvents, calLoading } = useCalendar();
  const now = new Date();

  // Only consider events that haven't fired yet (with 5-min post-fire buffer)
  const POST_FIRE_MS = 5 * 60 * 1000;
  const highImpact = todayEvents.filter(e =>
    e.impact === "high" && e.dt.getTime() > now.getTime() - POST_FIRE_MS
  );

  const blocks = TRADING_BLOCKS.map(block => ({
    ...block,
    ...getBlockStatus(block, highImpact),
  }));

  // Current block
  const etOpts  = { timeZone: "America/New_York" };
  const nowET   = new Date(now.toLocaleString("en-US", etOpts));
  const nowMins = nowET.getHours() * 60 + nowET.getMinutes();

  const statusStyle = {
    clean:   { color: "#22c55e", bg: "#22c55e10", border: "#22c55e30", icon: "✅", label: "Clean"   },
    caution: { color: "#f59e0b", bg: "#f59e0b10", border: "#f59e0b30", icon: "⚠️", label: "Caution" },
    avoid:   { color: "#ef4444", bg: "#ef444410", border: "#ef444430", icon: "🚫", label: "Avoid"   },
  };

  // Next clean window
  const nextClean = blocks.find(b => {
    const bMins = blockToMinutes(b.startH, b.startM);
    return b.status === "clean" && bMins > nowMins;
  });

  if (calLoading) return (
    <div style={{ padding: "12px 0", textAlign: "center", fontSize: 12, color: "#6b7280" }}>
      Loading today's trading windows...
    </div>
  );

  const currentBlock = blocks.find(b =>
    nowMins >= blockToMinutes(b.startH, b.startM) &&
    nowMins <  blockToMinutes(b.endH, b.endM)
  );

  const fmt12 = (h, m) => {
    const ampm = h >= 12 ? "PM" : "AM";
    const h12  = h % 12 || 12;
    return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
  };

  return (
    <div style={{ marginTop: 4 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 14, paddingBottom: 10,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}>
        <span style={{ fontSize: 16 }}>🕐</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>
            Today's Trading Windows
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>
            Calendar-aware time blocks for today — based on high-impact events ±30 min.
          </div>
        </div>
      </div>

      {/* No events — all clear */}
      {highImpact.length === 0 && (
        <div style={{
          padding: "10px 14px", borderRadius: 8,
          background: "#22c55e10", border: "1px solid #22c55e30",
          fontSize: 12, color: "#22c55e", marginBottom: 12,
        }}>
          🎯 No high-impact events today — all windows are clean.
        </div>
      )}

      {/* Current window callout */}
      {currentBlock && (
        <div style={{
          padding: "8px 14px", borderRadius: 8, marginBottom: 12,
          background: statusStyle[currentBlock.status].bg,
          border: `1px solid ${statusStyle[currentBlock.status].border}`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 14 }}>{statusStyle[currentBlock.status].icon}</span>
          <div>
            <span style={{ fontSize: 12, fontWeight: 700, color: statusStyle[currentBlock.status].color }}>
              You are in: {currentBlock.label}
            </span>
            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>
              {fmt12(currentBlock.startH, currentBlock.startM)} – {fmt12(currentBlock.endH, currentBlock.endM)} ET
            </span>
            {currentBlock.triggerEvent && (
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                ↳ {currentBlock.triggerEvent.event} at {currentBlock.triggerEvent.time}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next clean window hint */}
      {currentBlock && currentBlock.status !== "clean" && nextClean && (
        <div style={{
          padding: "7px 14px", borderRadius: 8, marginBottom: 12,
          background: "#22c55e08", border: "1px solid #22c55e20",
          fontSize: 11, color: "#22c55e",
        }}>
          💡 Next clean window: <strong>{nextClean.label}</strong> — {fmt12(nextClean.startH, nextClean.startM)} ET
        </div>
      )}

      {/* All blocks */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {blocks.map(b => {
          const s  = statusStyle[b.status];
          const isCurrent = currentBlock?.id === b.id;
          const isPast = nowMins >= blockToMinutes(b.endH, b.endM);
          return (
            <div key={b.id} style={{
              display: "grid", gridTemplateColumns: "20px 110px 1fr 60px",
              alignItems: "center", gap: 10,
              padding: "8px 12px", borderRadius: 8,
              background: isCurrent ? s.bg : "transparent",
              border: `1px solid ${isCurrent ? s.border : "rgba(255,255,255,0.05)"}`,
              opacity: isPast ? 0.45 : 1,
            }}>
              <span style={{ fontSize: 13 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? s.color : "#cbd5e1" }}>
                  {b.label}
                </div>
                <div style={{ fontSize: 10, color: "#6b7280" }}>
                  {fmt12(b.startH, b.startM)}–{fmt12(b.endH, b.endM)}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                {b.triggerEvent
                  ? `${b.triggerEvent.event} @ ${b.triggerEvent.time}`
                  : b.status === "clean" ? "No events" : ""}
              </div>
              <div style={{
                fontSize: 10, fontWeight: 700, textAlign: "right",
                color: s.color, letterSpacing: "0.05em",
              }}>
                {s.label.toUpperCase()}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 10, color: "#6b7280", marginTop: 10, textAlign: "center", lineHeight: 1.6 }}>
        Times shown in ET. Avoid and Caution windows are based on ±30 min around high-impact USD events.
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────

export function TradeChecklist({ setView, userId, currentAccountId, accounts = [], trades = [], tradesLoading = false, profile = null }) {
  const storageKey = userId ? `fos_checklist_v3_${userId}` : "fos_checklist_v3_demo";

  const defaultChecklist = [
    { id: "phys_rested",    text: "Do you feel well-rested?",                                          category: "Physical",      weight: 12, type: "positive" },
    { id: "phys_fed",       text: "Have you eaten in the last 4 hours?",                               category: "Physical",      weight: 8,  type: "positive" },
    { id: "env_workspace",  text: "Is your workspace clear and organized?",                            category: "Environmental", weight: 8,  type: "positive" },
    { id: "env_focus",      text: "Are you free from interruptions for the next 30 minutes?",          category: "Environmental", weight: 10, type: "positive" },
    { id: "mental_settled", text: "Have you taken at least 10 minutes to settle in before trading?",   category: "Mental",        weight: 10, type: "positive" },
    { id: "mental_warmup",  text: "Have you warmed up with a few practice trades today?",              category: "Mental",        weight: 8,  type: "positive" },
    { id: "mental_clear",   text: "Is your mind clear of yesterday's last trade?",                     category: "Mental",        weight: 12, type: "positive" },
    { id: "emo_ok_lose",    text: "Are you ok losing this trade?",                                     category: "Emotional",     weight: 13, type: "positive" },
    { id: "disc_plan",      text: "Are you trading from a plan, not from a feeling?",                  category: "Discipline",    weight: 17, type: "positive" },
    { id: "disc_walkaway",  text: "Are you willing to walk away if this setup doesn't materialize?",   category: "Discipline",    weight: 10, type: "positive" },
  ];

  const [items, setItems] = useLocalStorage(storageKey, defaultChecklist);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("check");

  const [newText, setNewText] = useState("");
  const [newCategory, setNewCategory] = useState("Mental");
  const [newWeight, setNewWeight] = useState(10);
  const [newType, setNewType] = useState("positive");

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editWeight, setEditWeight] = useState(10);
  const [editType, setEditType] = useState("positive");

  // Local account selection — MUST be declared after all other hooks
  // to preserve hook order. Defaults to the globally selected account.
  // Changing it here only affects which detector patterns are shown.
  const [selectedAccountId, setSelectedAccountId] = useState(currentAccountId || "");

  // Sync selectedAccountId when currentAccountId resolves after mount.
  // useState initializer only runs once — if currentAccountId was null on
  // first render, selectedAccountId stays "" without this sync.
  useEffect(() => {
    if (currentAccountId && !selectedAccountId) {
      setSelectedAccountId(currentAccountId);
    }
  }, [currentAccountId, selectedAccountId]);

  // ─── Detector results ─────────────────────────────────────────────────────
  // Run on mount so results are ready before the user finishes the checklist.
  // Scoped to selectedAccountId so switching accounts re-fires correctly.
  // Uses String() coercion to avoid UUID type mismatches.
  const effectiveAccountId = selectedAccountId || currentAccountId || null;
  const accountTrades = useMemo(
    () => effectiveAccountId
      ? trades.filter(t => String(t.account_id) === String(effectiveAccountId))
      : trades,
    [trades, effectiveAccountId]
  );
  const { rules: accountRules = [] } = useUserRules(userId, effectiveAccountId);
  const { data: allDetectorResults, loading: detectorsLoading } = useDetectors({
    suite: "all",
    trades: accountTrades,
    profile,
    rules: accountRules,
    userId,
    tradesLoading,
  });

  const totalPossibleScore = items.reduce((sum, item) => sum + (item.weight || 10), 0);

  const currentScore = Object.keys(answers).reduce((sum, id) => {
    const item = items.find(i => i.id === id);
    if (!item) return sum;
    const weight = item.weight || 10;
    const isPositive = item.type === "positive";
    return sum + (answers[id] === true ? (isPositive ? weight : -weight) : 0);
  }, 0);

  const percentage = totalPossibleScore > 0
    ? Math.round(Math.max(0, currentScore) / totalPossibleScore * 100)
    : 0;

  const allAnswered = items.every(item => answers[item.id] !== undefined);

  const evaluate = () => {
    const weakAreas = items.filter(i => answers[i.id] === false).map(i => i.text).slice(0, 4);
    let res, r;
    if (percentage >= 85) {
      res = "take";
      r = "Excellent prep. You're aligned across the board — you're in a strong position to trade this session.";
    } else if (percentage >= 65) {
      res = "risky";
      r = `Decent, but a few weak spots: ${weakAreas.join(", ")}. Consider trading smaller and staying extra disciplined this session.`;
    } else {
      res = "no";
      r = `Too many problems right now: ${weakAreas.join(", ")}. Consider sitting this session out until conditions are cleaner.`;
    }
    setResult(res);
    setReason(r);
  };

  const reset = () => { setAnswers({}); setResult(null); setReason(""); };
  const resetToDefault = () => { setItems(defaultChecklist); reset(); };

  const addItem = () => {
    if (!newText.trim()) return;
    setItems(prev => [...prev, { id: `custom_${Date.now()}`, text: newText.trim(), category: newCategory, weight: newWeight, type: newType }]);
    setNewText("");
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const saveEdit = (id) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, text: editText, weight: editWeight, type: editType } : i));
    setEditingId(null);
  };

  const moveItem = (id, dir) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === id);
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const configs = {
    take:  { label: "✅ READY TO TRADE",        color: C.green  },
    risky: { label: "⚠️ PROCEED WITH CAUTION", color: C.yellow },
    no:    { label: "❌ NOT READY",             color: C.red    },
  };

  const grouped = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <button onClick={() => setView("dashboard")} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, padding: 0 }}>‹ Dashboard</button>
        <h2 style={{ margin: "8px 0 4px", fontSize: 20, fontWeight: 800 }}>Pre-Flight Check</h2>
        <p style={{ color: C.muted, fontSize: 12 }}>Are you ready to trade? • {percentage}% score</p>

        {/* Account selector — scopes behavioral patterns to the right account */}
        {accounts.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 11, color: C.muted, marginBottom: 5, display: "block", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Trading Account
            </label>
            <select
              value={selectedAccountId}
              onChange={e => setSelectedAccountId(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px",
                background: "#1a1d2e", border: `1px solid ${C.border}`,
                borderRadius: 8, color: C.text, fontSize: 13,
              }}
            >
              <option value="">All Accounts</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} — {acc.account_type?.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Header Buttons */}
      <div className="fos-btn-row" style={{ display: "flex", gap: 8, marginBottom: 16, justifyContent: "flex-end" }}>
        {mode === "check" && result && <Btn variant="ghost" onClick={reset} style={{ fontSize: 12, padding: "7px 14px" }}>Reset</Btn>}
        <Btn
          variant={mode === "edit" ? "primary" : "ghost"}
          onClick={() => { setMode(m => m === "check" ? "edit" : "check"); reset(); }}
          style={{ fontSize: 12, padding: "7px 14px" }}
        >
          {mode === "edit" ? "✓ Done Editing" : "✏️ Edit Checklist"}
        </Btn>
      </div>

      {/* CHECK MODE */}
      {mode === "check" && !result && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {Object.entries(grouped).map(([category, catItems]) => (
            <Card key={category}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>{CATEGORY_ICONS[category] || "⭐"}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{category}</span>
              </div>
              {catItems.map(item => {
                const ans = answers[item.id];
                return (
                  <div key={item.id} style={{ padding: "12px 0", borderTop: "1px solid #1c1f30" }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{item.text}</p>
                        <p style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Weight: {item.weight}</p>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => setAnswers(p => ({...p, [item.id]: true}))}
                          style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${ans === true ? C.green : C.border}`, background: ans === true ? C.green + "20" : "transparent", color: ans === true ? C.green : C.muted, cursor: "pointer", fontSize: 12 }}>
                          Yes
                        </button>
                        <button onClick={() => setAnswers(p => ({...p, [item.id]: false}))}
                          style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${ans === false ? C.red : C.border}`, background: ans === false ? C.red + "20" : "transparent", color: ans === false ? C.red : C.muted, cursor: "pointer", fontSize: 12 }}>
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Card>
          ))}

          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Current Score: <span style={{ color: C.blue }}>{percentage}%</span></div>
          </div>

          <Btn onClick={evaluate} disabled={!allAnswered} style={{ width: "100%", padding: 14 }}>
            Get My Answer →
          </Btn>
        </div>
      )}

      {/* RESULT — verdict + behavioral patterns */}
      {mode === "check" && result && (
        <Card glow={configs[result].color} style={{ padding: 32 }}>
          {/* Verdict */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: configs[result].color }}>
              {configs[result].label}
            </div>
            <p style={{ color: C.text, fontSize: 15, lineHeight: 1.6, marginTop: 16 }}>
              {reason}
            </p>
            <div style={{
              marginTop: 16, padding: "8px 12px",
              background: "#1a1d2e",
              border: `1px solid ${C.border}`,
              borderRadius: 6, fontSize: 11, color: C.muted, lineHeight: 1.5,
              textAlign: "left",
            }}>
              <strong style={{ color: C.yellow }}>⚠ Not financial advice.</strong>
              {" "}This checklist reflects your own stated readiness for this session, not a recommendation
              on any specific trade. The decision to trade is always yours.
            </div>
          </div>

          {/* Divider before patterns */}
          <div style={{ borderTop: `1px solid ${C.border}`, margin: "24px 0" }} />

          {/* Behavioral patterns — costly patterns to watch, side by side with confirmed strengths */}
          {userId
            ? (
              <div style={{
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}>
                <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                  <ActivePatternsBlock allDetectorResults={allDetectorResults} detectorsLoading={detectorsLoading} />
                </div>
                <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                  <StrengthsBlock allDetectorResults={allDetectorResults} detectorsLoading={detectorsLoading} />
                </div>
              </div>
            )
            : <div style={{ textAlign: "center", fontSize: 12, color: C.muted }}>Sign in to see your behavioral patterns here.</div>
          }

          {/* Today's Trading Window */}
          <div style={{ borderTop: `1px solid ${C.border}`, margin: "24px 0" }} />
          <TodaysTradingWindow />

          <Btn variant="ghost" onClick={reset} style={{ marginTop: 28, width: "100%" }}>
            Check Again
          </Btn>
        </Card>
      )}

      {/* EDIT MODE */}
      {mode === "edit" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <SectionTitle>Your Personal Checklist</SectionTitle>
            {items.map((item, idx) => (
              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: idx > 0 ? `1px solid ${C.border}20` : "none" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                  <button onClick={() => moveItem(item.id, -1)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 10 }}>▲</button>
                  <button onClick={() => moveItem(item.id, 1)}  style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 10 }}>▼</button>
                </div>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{CATEGORY_ICONS[item.category] || "⭐"}</span>
                {editingId === item.id ? (
                  <>
                    <input value={editText} onChange={e => setEditText(e.target.value)} style={{ flex: 1, background: "#1a1d2e", border: `1px solid ${C.blue}`, borderRadius: 6, padding: "6px 10px", color: C.text }} />
                    <input type="number" value={editWeight} onChange={e => setEditWeight(+e.target.value)} style={{ width: 70, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px" }} />
                    <select value={editType} onChange={e => setEditType(e.target.value)} style={{ background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px" }}>
                      <option value="positive">Positive (+)</option>
                      <option value="negative">Negative (-)</option>
                    </select>
                    <button onClick={() => saveEdit(item.id)} style={{ background: C.green + "18", border: "none", borderRadius: 6, padding: "6px 12px", color: C.green }}>Save</button>
                    <button onClick={() => setEditingId(null)}  style={{ background: "none", border: "none", color: C.muted }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 12, color: C.text }}>{item.text}</span>
                    <span style={{ fontSize: 11, color: C.muted }}>({item.weight})</span>
                    <button onClick={() => { setEditingId(item.id); setEditText(item.text); setEditWeight(item.weight || 10); setEditType(item.type || "positive"); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 8px", color: C.sub }}>✏️</button>
                    <button onClick={() => removeItem(item.id)} style={{ background: "none", border: "none", color: C.muted }}>✕</button>
                  </>
                )}
              </div>
            ))}
          </Card>

          <Card>
            <SectionTitle>Add New Item</SectionTitle>
            <div style={{ padding: "8px 12px", background: "rgba(0,0,0,0.25)", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, color: C.muted, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Total checklist weight: <strong style={{ color: C.text, fontFamily: "monospace" }}>{items.reduce((s, i) => s + (i.weight || 0), 0)}</strong></span>
              <span style={{ fontSize: 10 }}>{items.length} item{items.length === 1 ? "" : "s"}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input value={newText} onChange={e => setNewText(e.target.value)} placeholder="New checklist item" style={{ width: "100%", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px", color: C.text }} />
              <div style={{ display: "flex", gap: 10 }}>
                <select value={newCategory} onChange={e => setNewCategory(e.target.value)} style={{ flex: 1, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px", color: C.text }}>
                  {CATEGORY_ORDER.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="number" value={newWeight} min={1} max={30} onChange={e => setNewWeight(+e.target.value)} style={{ width: 80, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px", color: C.text }} />
                <select value={newType} onChange={e => setNewType(e.target.value)} style={{ background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px", color: C.text }}>
                  <option value="positive">Positive (+)</option>
                  <option value="negative">Negative (-)</option>
                </select>
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: -4, lineHeight: 1.5 }}>
                Suggested weight: 5–15. Higher weight = the question matters more to the overall score.
              </div>
              <Btn onClick={addItem} disabled={!newText.trim()}>+ Add Item</Btn>
            </div>
          </Card>

          <button onClick={resetToDefault} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px", color: C.muted, cursor: "pointer" }}>
            ↺ Reset to Default Checklist
          </button>
        </div>
      )}
    </div>
  );
}
